const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to create headers
const getHeaders = (includeAuth = false) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  },

  register: async (username, email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username, email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    return await response.json();
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  isAuthenticated: () => {
    return !!getAuthToken();
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

// News API
export const newsAPI = {
  // Get all published news (public)
  getAllNews: async () => {
    const response = await fetch(`${API_BASE_URL}/news`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch news');
    }

    return await response.json();
  },

  // Get single news post (public)
  getNewsById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/news/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch news post');
    }

    return await response.json();
  },

  // Get all news for admin (including drafts)
  getAllNewsAdmin: async () => {
    const response = await fetch(`${API_BASE_URL}/news/admin/all`, {
      headers: getHeaders(true),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch news');
    }

    return await response.json();
  },

  // Create news post
  createNews: async (formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/news`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // FormData for file upload
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create news post');
    }

    return await response.json();
  },

  // Update news post
  updateNews: async (id, formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/news/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update news post');
    }

    return await response.json();
  },

  // Delete news post
  deleteNews: async (id) => {
    const response = await fetch(`${API_BASE_URL}/news/${id}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete news post');
    }

    return await response.json();
  },

  // Add additional images to post
  addImages: async (postId, formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/news/${postId}/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add images');
    }

    return await response.json();
  },
};
